import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Flame, Plus, Trash, MapPin } from "@phosphor-icons/react";

/**
 * Admin Smart-Event-Hotspot configuration card.
 *
 * Exposes:
 *  - min-trucks threshold (default 3)
 *  - blacklist coords (suppress hotspot formation at commissaries /
 *    storage lots / known false-positive venues)
 *
 * Reads/writes a single `system_state` doc — admins can tune
 * detection without a redeploy. Feature flag ON/OFF for the whole
 * system lives in the existing Feature Flags card.
 */
export default function HotspotConfigCard() {
  const [config, setConfig] = useState(null);
  const [minTrucks, setMinTrucks] = useState(3);
  const [blacklist, setBlacklist] = useState([]); // [{lat, lng, radius_m, label}]
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newLat, setNewLat] = useState("");
  const [newLng, setNewLng] = useState("");
  const [newRadius, setNewRadius] = useState("60");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hotspot-config");
      setConfig(data);
      setMinTrucks(data.min_trucks || 3);
      setBlacklist(Array.isArray(data.blacklist) ? data.blacklist : []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load hotspot config"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/hotspot-config", {
        min_trucks: Number(minTrucks),
        blacklist,
      });
      toast.success("Hotspot config saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const addBlacklistEntry = () => {
    const lat = parseFloat(newLat);
    const lng = parseFloat(newLng);
    const radius = parseFloat(newRadius) || 60;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast.error("Enter valid lat/lng");
      return;
    }
    setBlacklist((prev) => [
      ...prev,
      { lat, lng, radius_m: radius, label: newLabel.trim() || undefined },
    ]);
    setNewLabel(""); setNewLat(""); setNewLng(""); setNewRadius("60");
  };

  const removeAt = (i) => {
    setBlacklist((prev) => prev.filter((_, idx) => idx !== i));
  };

  return (
    <section className="mb-6" data-testid="hotspot-config-card">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Flame size={18} weight="duotone" className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight" data-testid="hotspot-config-heading">
            Smart Event Hotspots — tuning
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Tune detection without a redeploy. The system flag ON/OFF lives in <span className="font-medium text-foreground">Feature Flags</span>.
          Trucks at any blacklisted coord are excluded from hotspot formation — use for commissaries, storage lots,
          and venues that produce false positives.
        </p>

        {loading ? (
          <div className="text-sm text-muted-foreground py-3">Loading…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Min trucks to qualify</div>
                <Input
                  type="number"
                  min={2}
                  max={20}
                  value={minTrucks}
                  onChange={(e) => setMinTrucks(e.target.value)}
                  data-testid="hotspot-min-trucks-input"
                />
              </label>
              <div className="block">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Radius (meters)</div>
                <div className="h-10 px-3 rounded-md border border-border bg-background/50 flex items-center text-sm text-muted-foreground">
                  {config?.radius_m ?? 150} m <span className="ml-2 text-[10px] uppercase">fixed</span>
                </div>
              </div>
              <div className="block">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Blacklist entries</div>
                <div className="h-10 px-3 rounded-md border border-border bg-background/50 flex items-center text-sm">
                  {blacklist.length}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-background/30 p-4 mb-3">
              <div className="text-xs font-semibold text-foreground mb-2 inline-flex items-center gap-1.5">
                <MapPin size={14} weight="duotone" /> Blacklist coords
              </div>
              {blacklist.length === 0 ? (
                <div className="text-xs text-muted-foreground py-1">None — every overlap of ≥{minTrucks} trucks within {config?.radius_m ?? 150}m forms a hotspot.</div>
              ) : (
                <ul className="divide-y divide-border mb-3">
                  {blacklist.map((b, i) => (
                    <li key={`${b.lat}|${b.lng}|${i}`} className="py-2 flex items-center gap-3 text-sm" data-testid={`hotspot-blacklist-row-${i}`}>
                      <span className="font-medium flex-1 truncate">{b.label || "(unlabeled)"}</span>
                      <span className="text-xs text-muted-foreground font-mono tabular-nums">
                        {b.lat.toFixed(5)}, {b.lng.toFixed(5)} · {b.radius_m || 60}m
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAt(i)}
                        className="h-7 w-7 rounded-md hover:bg-destructive/10 text-destructive grid place-items-center"
                        aria-label="Remove entry"
                        data-testid={`hotspot-blacklist-remove-${i}`}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid sm:grid-cols-5 gap-2">
                <Input
                  placeholder="Label (optional)"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="sm:col-span-2"
                  data-testid="hotspot-blacklist-label-input"
                />
                <Input
                  placeholder="Lat"
                  value={newLat}
                  onChange={(e) => setNewLat(e.target.value)}
                  data-testid="hotspot-blacklist-lat-input"
                />
                <Input
                  placeholder="Lng"
                  value={newLng}
                  onChange={(e) => setNewLng(e.target.value)}
                  data-testid="hotspot-blacklist-lng-input"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Radius m"
                    value={newRadius}
                    onChange={(e) => setNewRadius(e.target.value)}
                    data-testid="hotspot-blacklist-radius-input"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addBlacklistEntry} data-testid="hotspot-blacklist-add-btn">
                    <Plus size={14} weight="bold" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="hotspot-config-save-btn">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
